import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react'
import { useEffectOnce } from 'react-use'
import { Icon } from '../../icon'
import { Button } from '../../button'
import { ContextShell } from '../../../modules/context'
import styles from './styles.module.scss'

type Props = {
  show: boolean
  image: string | null
  title: string | null
  description: string | null
  buttonText: string | null
  loading: boolean
  onClickPaywall: () => void
  onClosePaywall: (e: React.MouseEvent<HTMLButtonElement>) => void
}

export const REACTIVATE_SUBSCRIPTION_BANNER_ID =
  'moises:banner_reactivate_subscription'

export const ReactivateSubscriptionBanner: React.FC<Props> = ({
  show,
  image,
  title,
  description,
  buttonText,
  loading,
  onClickPaywall,
  onClosePaywall
}) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [delayToShow, setDelayToShow] = useState(true)

  const { event } = useContext(ContextShell)
  const { sendEventPostHog } = event

  const sendEventOneTime = useCallback(() => {
    if (!show) return

    const bannerViewed = localStorage.getItem(REACTIVATE_SUBSCRIPTION_BANNER_ID)

    if (!bannerViewed) {
      localStorage.setItem(REACTIVATE_SUBSCRIPTION_BANNER_ID, 'true')

      sendEventPostHog({
        name: 'purchase_flow:reactivate_subscription_banner_view',
        event_category: 'growth',
        customAttributes: {
          type: 'web_sidebar_banner',
          source: 'reactivate-subscription-5-active-projects'
        }
      })
    }
  }, [show, sendEventPostHog])

  useEffectOnce(() => {
    sendEventOneTime()
  })

  useEffect(() => {
    if (show) {
      timeoutRef.current = setTimeout(() => {
        setDelayToShow(false)
      }, 600)
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      setDelayToShow(true)
    }
  }, [show])

  if (!show || delayToShow) {
    return null
  }

  return (
    <div
      aria-hidden
      role="button"
      className={styles.wrapper}
      onClick={onClickPaywall}
    >
      <div
        className={styles.background}
        style={{ backgroundImage: `url(${image})` }}
      />
      <button
        type="button"
        aria-label="Close"
        className={styles.close}
        onClick={onClosePaywall}
      >
        <Icon name="close" width={20} height={20} />
      </button>
      <div className={styles.caption}>
        <div className={styles.header}>
          <p className={styles.title}>{title}</p>
          <p className={styles.description}>{description}</p>
        </div>
        <Button
          long
          className={styles.cta}
          title={
            loading ? (
              <Icon name="loader" animation="spin" width={18} height={18} />
            ) : (
              buttonText
            )
          }
          disabled={loading}
          newTab={false}
        />
      </div>
    </div>
  )
}
